
import React from "react";
import { Eye, Edit, Send, MoreHorizontal, Square, Play, Copy, Trash2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Campaign } from "@/types/campaigns";
import { Campaign as LegacyCampaign } from "../CampaignModal";

interface CampaignActionsProps {
  campaign: Campaign;
  status: "draft" | "active" | "scheduled" | "completed";
  openEditModal: (campaign: LegacyCampaign) => void;
  openDeleteDialog: (campaign: Campaign) => void;
  openPublishDialog: (campaign: Campaign) => void;
  openResultsSheet: (campaign: LegacyCampaign) => void;
  openViewModal: (campaign: Campaign) => void;
  onDuplicate: (campaign: Campaign) => void;
  onFinalize: (campaign: Campaign) => void;
  onReactivate?: (campaign: Campaign) => void;
}

const CampaignActions = ({
  campaign,
  status,
  openEditModal,
  openDeleteDialog,
  openPublishDialog,
  openResultsSheet,
  openViewModal,
  onDuplicate,
  onFinalize,
  onReactivate
}: CampaignActionsProps) => {
  const formatCampaignForModal = (campaign: Campaign): LegacyCampaign => ({
    id: campaign.id,
    name: campaign.name,
    type: campaign.type,
    product: campaign.product,
    value: campaign.value,
    start_date: campaign.start_date,
    end_date: campaign.end_date,
    status: campaign.status as "active" | "scheduled" | "completed" | "draft",
    description: campaign.description,
    minimum_spent_amount: campaign.minimum_spent_amount,
    cashback_template_id: campaign.cashback_template_id,
    max_coupons_per_customer: campaign.max_coupons_per_customer,
    delay_days: campaign.delay_days,
    use_custom_cashback: campaign.use_custom_cashback,
    custom_cashback_percentage: campaign.custom_cashback_percentage,
    custom_cashback_multiplier: campaign.custom_cashback_multiplier,
    custom_cashback_expiration_days: campaign.custom_cashback_expiration_days,
  });

  const getDeleteButtonText = (campaignStatus: string) => {
    switch (campaignStatus) {
      case 'active':
        return 'Finalizar';
      case 'scheduled':
        return 'Cancelar';
      default:
        return 'Excluir';
    }
  };

  const canReactivate = (campaign: Campaign) => {
    if (status !== "completed") return false;
    const endDate = new Date(campaign.end_date);
    const today = new Date();
    return endDate >= today;
  };

  const renderPrimaryAction = () => {
    if (status === "draft") {
      return (
        <Button 
          variant="brand" 
          size="sm"
          onClick={() => openPublishDialog(campaign)}
          className="text-white"
        >
          <Send className="h-4 w-4 mr-1" /> 
          Publicar
        </Button>
      );
    }
    
    if (status === "active" || status === "completed") {
      return (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => openResultsSheet(formatCampaignForModal(campaign))}
          className="border-blue-200 text-blue-700 hover:bg-blue-50"
        >
          <Eye className="h-4 w-4 mr-1" /> 
          Resultados
        </Button>
      );
    }

    if (status === "scheduled") {
      return (
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => openEditModal(formatCampaignForModal(campaign))}
        >
          <Edit className="h-4 w-4 mr-1" /> 
          Editar
        </Button>
      );
    }

    return null;
  };

  const renderSecondaryActions = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Abrir menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={() => openViewModal(campaign)}>
          <Search className="h-4 w-4 mr-2" />
          Visualizar
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {status === "active" && (
          <>
            <DropdownMenuItem onClick={() => onFinalize(campaign)}>
              <Square className="h-4 w-4 mr-2" />
              Finalizar campanha
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        
        {status === "completed" && canReactivate(campaign) && onReactivate && (
          <>
            <DropdownMenuItem onClick={() => onReactivate(campaign)}>
              <Play className="h-4 w-4 mr-2" />
              Reativar campanha
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        
        <DropdownMenuItem onClick={() => openEditModal(formatCampaignForModal(campaign))}>
          <Edit className="h-4 w-4 mr-2" />
          Editar
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={() => onDuplicate(campaign)}>
          <Copy className="h-4 w-4 mr-2" />
          Duplicar
        </DropdownMenuItem>
        
        {status !== "completed" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => openDeleteDialog(campaign)}
              className="text-red-600 focus:text-red-600 focus:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {getDeleteButtonText(campaign.status)}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="flex items-center justify-end gap-2">
      {renderPrimaryAction()}
      {renderSecondaryActions()}
    </div>
  );
};

export default CampaignActions;
